import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { dioptre } from '@lensmira/domain'

import { useTranslation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * Raising the job that makes the glasses. §6.8 `JOB-001`, `JOB-002`. Posts to `POST /jobs`,
 * `apps/api/src/jobs/jobs.service.ts`.
 *
 * **A confirmed sale for spectacles could not reach the lab from anywhere in this product.**
 * `POST /jobs` has been complete for a long time and `job_create_advance_status` is held by the
 * owner, the manager, the dispenser and the optician, so the one act the shop exists to perform
 * was the act with no screen. The work queue listed jobs; nothing made one.
 *
 * **Why the figures are pre-filled here, when the invoice desk refuses to pre-fill anything.**
 * `JOB-002` is a frozen snapshot: "a job holds the Rx, frame, lens spec and measurements as they
 * were at order time, and a later Rx change never rewrites what was manufactured". Copying the
 * prescription in is therefore the operation, not a convenience, and the opposite of the
 * three-way match, where a pre-filled quantity makes a variance unreachable. The version the
 * figures came from is named on screen, so an accepted snapshot can be traced to the Rx it was
 * taken from.
 *
 * They stay editable, and that is deliberate. The server takes the values rather than the
 * prescription id, a shop working from a paper Rx has nothing to pre-fill from, and `RX-001`'s
 * record is the optometrist's while the snapshot is the shop's instruction to a lab. What a
 * person sends is what a person accepted.
 *
 * **What this panel does not send, and why neither is an oversight.**
 *
 * - **No `orderLineId`.** `CreateJob` takes one and nothing in this repository has ever set it.
 *   `later_todo.md` 17 is the reason: `od` and `os` make one job a pair, `PRC-007` prices one lens
 *   per line, so a pair is two lines and one id cannot name both. The link the spec wants is
 *   `job.pair_number`, which is a product call. Left null, as every other path leaves it.
 * - **No `lensProductId` and no `diameterMm`.** Both are on `EyeSnapshot` and no read reaching
 *   this screen carries either: `OrderDetailLine` has a description and a price and no lens
 *   product. That is the same blocker, so the panel asks for neither rather than offering a box
 *   somebody would fill in from memory into a record a lab manufactures from.
 *
 * **Every refusal the service gives is pre-checked, because a refused write stops the whole
 * queue** until somebody discards it (`OFF-001a`):
 *
 * - `ORDER_NOT_MANUFACTURABLE`: the status branch below mirrors the service's own denylist, which
 *   is `draft`, `quoted`, `expired`, `cancelled` and `refunded`. A `confirmed`, a `paid`, a
 *   `partially_paid` and a status this build has never heard of are all offered, per `ARC-020`.
 * - `SNAPSHOT_NOT_QUARTER_STEP`: checked with `dioptre()`, the same domain primitive the service
 *   calls, so the two cannot drift. The service's own comment records what happens without this:
 *   the column constraint surfaces as a `PostgresError`, which becomes a 500, which the queue
 *   classifies as unreachable and retries forever.
 * - `FIELD_REQUIRED` for an eye with no sphere or cylinder, and for a job with no eye at all.
 *   `JOB-001` is "one pair (or one lens)", so an eye left empty is absent rather than plano: the
 *   substituted zero is a lens nobody ordered.
 * - `OWN_FRAME_*`, `CAT-007`: a lenses-only sale cannot be sent without the customer's own frame
 *   described and the breakage-liability policy acknowledged. The acknowledgement box starts
 *   unticked and is not enabled until a policy version has been typed, so the act is a deliberate
 *   one with the version in front of the person doing it. The staff member and the moment are the
 *   server's, never sent from here (`BR-08`).
 */

/** One eye as the prescription read gives it. Only the four fields a snapshot takes. */
export interface SendToLabEye {
  readonly sphere: number
  readonly cylinder: number
  readonly axis?: number
  readonly add?: number
}

/** The Rx the sale was made against, and the measurements taken for it. */
export interface SendToLabRx {
  /** `RX-002`'s version number, named on screen so the snapshot is traceable. */
  readonly version: number
  readonly od?: SendToLabEye
  readonly os?: SendToLabEye
  readonly monocularPdOd?: number
  readonly monocularPdOs?: number
  readonly segHeightOd?: number
  readonly segHeightOs?: number
}

export interface SendToLabProps {
  readonly orderId: string
  /**
   * What the shop calls the sale, or null until `TAX-002` numbers it.
   *
   * Nullable rather than taking a label, because the label goes into a sentence here and
   * "A void on Not invoiced yet" is not one. The panel picks the sentence that fits.
   */
  readonly orderNumber: string | null
  /** Appendix C.1's state, a `string` per `ARC-020`. */
  readonly status: string
  /** `BR-01`, `BR-08`: the job is created at the branch the caller names. */
  readonly branchId: string
  /**
   * The Rx version the order names, when it could be read.
   *
   * Absent means the sale has no customer, or no prescription, or the reader does not hold
   * `prescription_view`. The form still opens: the server takes values, not an id.
   */
  readonly rx?: SendToLabRx
  /** The order's own `prescriptionId`, sent so the job records which Rx it was taken from. */
  readonly prescriptionId?: string
  /**
   * The order's sale type. `lenses_only_own_frame` makes the own-frame section mandatory; any
   * other type offers it behind an unticked choice, for a pair in a mixed sale.
   */
  readonly saleType?: string
  /** Jobs already being made from this sale, so a second one is a decision rather than an accident. */
  readonly jobs: readonly { readonly jobNumber: string; readonly status: string }[]
  readonly onRaised?: () => void
}

interface EyeForm {
  sphere: string
  cylinder: string
  axis: string
  add: string
  pd: string
  seg: string
}

const EMPTY: EyeForm = { sphere: '', cylinder: '', axis: '', add: '', pd: '', seg: '' }

/** A number as a box holds it: blank, or the figure, never a zero standing in for blank. */
const shown = (value: number | undefined): string => (value === undefined ? '' : String(value))

const eyeForm = (eye: SendToLabEye | undefined, pd?: number, seg?: number): EyeForm =>
  eye === undefined
    ? { ...EMPTY, pd: shown(pd), seg: shown(seg) }
    : {
        sphere: String(eye.sphere),
        cylinder: String(eye.cylinder),
        axis: shown(eye.axis),
        add: shown(eye.add),
        pd: shown(pd),
        seg: shown(seg),
      }

/** Statuses the service refuses outright. Its list, not a stricter one of this screen's own. */
const NOT_MANUFACTURABLE = ['draft', 'quoted', 'expired', 'cancelled', 'refunded']

class EyeError extends Error {}

export function SendToLab({
  orderId,
  orderNumber,
  status,
  branchId,
  rx,
  prescriptionId,
  saleType,
  jobs,
  onRaised,
}: SendToLabProps): JSX.Element | null {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [writeId, setWriteId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [promisedAt, setPromisedAt] = useState('')
  const [supplierId, setSupplierId] = useState('')
  const [od, setOd] = useState<EyeForm>(() => eyeForm(rx?.od, rx?.monocularPdOd, rx?.segHeightOd))
  const [os, setOs] = useState<EyeForm>(() => eyeForm(rx?.os, rx?.monocularPdOs, rx?.segHeightOs))
  /** Whether anybody has typed in the eyes yet. Nothing below overwrites a person who has. */
  const [typedIn, setTypedIn] = useState(false)
  /** `CAT-007`. Mandatory for a lenses-only sale; otherwise the dispenser's choice, unticked. */
  const ownFrameRequired = saleType === 'lenses_only_own_frame'
  const [ownFrameChosen, setOwnFrameChosen] = useState(false)
  const ownFrame = ownFrameRequired || ownFrameChosen
  const [frameDescription, setFrameDescription] = useState('')
  const [policyVersion, setPolicyVersion] = useState('')
  /** Never initialised to true and never set by anything but the person's own tick. */
  const [acknowledged, setAcknowledged] = useState(false)

  /**
   * Fill the eyes when the prescription arrives, which is not always before this panel exists.
   *
   * The initialisers above run once, and the shell loads the prescription in an effect, so a panel
   * that mounts first keeps empty boxes while the sentence over them says the figures are version
   * N of the prescription. That is what a browser showed: the right version named above fourteen
   * blank boxes. A dispenser reads "check them", sees nothing, and fills them in from memory, into
   * the record a lab manufactures from. Fifteen component tests could not see it, because every
   * one of them passes `rx` at mount.
   *
   * Keyed on the figures rather than on the object, so a parent that rebuilds an equal `rx` on
   * every render does not refill the form underneath somebody. Skipped once anybody has typed,
   * because a late read must not take a correction away.
   */
  const rxKey =
    rx === undefined
      ? ''
      : [
          rx.version,
          rx.od?.sphere,
          rx.od?.cylinder,
          rx.od?.axis,
          rx.od?.add,
          rx.os?.sphere,
          rx.os?.cylinder,
          rx.os?.axis,
          rx.os?.add,
          rx.monocularPdOd,
          rx.monocularPdOs,
          rx.segHeightOd,
          rx.segHeightOs,
        ].join(',')

  useEffect(() => {
    if (typedIn || rx === undefined) return
    setOd(eyeForm(rx.od, rx.monocularPdOd, rx.segHeightOd))
    setOs(eyeForm(rx.os, rx.monocularPdOs, rx.segHeightOs))
    // The dependency list is `rxKey` and not `rx`: the key stands in for the figures, and the
    // object itself would re-run this on every parent render. No disable comment, because
    // `react-hooks/exhaustive-deps` is not a rule this workspace configures and naming it is an
    // error in its own right.
  }, [rxKey, typedIn])

  useWriteSettled(snapshot, writeId, () => {
    // The parent re-reads, and the order comes back naming this job, which is the whole of what
    // this panel has to show afterwards: `order-detail.service.ts` carries `jobs` now.
    setWriteId(null)
    onRaised?.()
  })

  /**
   * One eye, or nothing when the eye is not being made.
   *
   * A quarter-step check on sphere, cylinder and add, with `dioptre()` itself rather than a
   * regular expression: the service calls the same function, so a value this accepts is a value
   * that one accepts. Axis, PD and segment height are not dioptric and are whole or decimal
   * millimetres and degrees, so they are checked as numbers only.
   */
  const snapshotOf = useCallback(
    (form: EyeForm, side: 'od' | 'os'): Record<string, number> | undefined => {
      const filled = Object.values(form).some((value) => value.trim() !== '')
      if (!filled) return undefined

      const sideWord = side === 'od' ? t('sendToLab.right') : t('sendToLab.left')
      const quarter = (text: string, field: string): number => {
        const value = Number(text.trim())
        if (text.trim() === '' || !Number.isFinite(value)) {
          throw new EyeError(t('sendToLab.eyeNumber', { side: sideWord, field }))
        }
        try {
          dioptre(value)
        } catch {
          throw new EyeError(t('sendToLab.eyeQuarterStep', { side: sideWord, field }))
        }
        return value
      }
      const plain = (text: string, field: string): number | undefined => {
        if (text.trim() === '') return undefined
        const value = Number(text.trim())
        if (!Number.isFinite(value)) {
          throw new EyeError(t('sendToLab.eyeNumber', { side: sideWord, field }))
        }
        return value
      }

      const axis = plain(form.axis, t('sendToLab.axis'))
      if (axis !== undefined && (!Number.isInteger(axis) || axis < 0 || axis > 180)) {
        // `RX-001`: an axis is 0 to 180 whole degrees. 181 is not a direction.
        throw new EyeError(t('sendToLab.axisRange', { side: sideWord }))
      }

      return {
        sphere: quarter(form.sphere, t('sendToLab.sphere')),
        cylinder: quarter(form.cylinder, t('sendToLab.cylinder')),
        ...(form.add.trim() === '' ? {} : { add: quarter(form.add, t('sendToLab.add')) }),
        ...(axis === undefined ? {} : { axis }),
        ...(plain(form.pd, t('sendToLab.pd')) === undefined
          ? {}
          : { monocularPdMm: plain(form.pd, t('sendToLab.pd')) as number }),
        ...(plain(form.seg, t('sendToLab.seg')) === undefined
          ? {}
          : { segmentHeightMm: plain(form.seg, t('sendToLab.seg')) as number }),
      }
    },
    [t],
  )

  const submit = useCallback(async () => {
    let right: Record<string, number> | undefined
    let left: Record<string, number> | undefined
    try {
      right = snapshotOf(od, 'od')
      left = snapshotOf(os, 'os')
    } catch (inputError) {
      setError(inputError instanceof EyeError ? inputError.message : t('sendToLab.cannotSend'))
      return
    }
    if (right === undefined && left === undefined) {
      // `JOB-001`: one pair or one lens, and neither is none.
      setError(t('sendToLab.oneEyeRequired'))
      return
    }

    if (ownFrame) {
      // The server refuses each of these with a 422, which stops the queue until somebody
      // discards it (`OFF-001a`), so they are said here first.
      if (frameDescription.trim() === '') {
        setError(t('sendToLab.ownFrameNeedsDescription'))
        return
      }
      if (policyVersion.trim() === '') {
        setError(t('sendToLab.ownFrameNeedsVersion'))
        return
      }
      if (!acknowledged) {
        setError(t('sendToLab.ownFrameNeedsAcknowledgement'))
        return
      }
    }

    setError(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/jobs',
      label: orderNumber === null
          ? t('sendToLab.queueLabelNoNumber')
          : t('sendToLab.queueLabel', { number: orderNumber }),
      body: {
        orderId,
        branchId,
        // No `expectedVersion`: `CreateJob` has no such field, and the job is a new row rather
        // than an edit of the order. The idempotency key is what makes a double tap one job.
        ...(prescriptionId === undefined ? {} : { prescriptionId }),
        ...(supplierId.trim() === '' ? {} : { supplierId: supplierId.trim() }),
        ...(promisedAt === '' ? {} : { promisedAt }),
        ...(right === undefined ? {} : { od: right }),
        ...(left === undefined ? {} : { os: left }),
        ...(ownFrame
          ? {
              ownFrame: {
                description: frameDescription.trim(),
                policyVersion: policyVersion.trim(),
                acknowledged: true,
              },
            }
          : {}),
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [
    acknowledged,
    branchId,
    enqueue,
    frameDescription,
    ownFrame,
    policyVersion,
    od,
    orderId,
    orderNumber,
    os,
    prescriptionId,
    promisedAt,
    snapshotOf,
    supplierId,
    t,
  ])

  const line = statusLine(t, snapshot, writeId, t('queue.subject.labJob'))

  if (NOT_MANUFACTURABLE.includes(status)) {
    // Said rather than offered, because the button would queue a 409 that holds the till.
    return (
      <section className="lm-send-lab" aria-labelledby="send-lab-heading">
        <h3 id="send-lab-heading" className="lm-send-lab__heading">
          {t('sendToLab.heading')}
        </h3>
        <p className="lm-hint" role="status">
          {t('sendToLab.notManufacturable', { status })}
        </p>
      </section>
    )
  }

  const eyeFields = (
    side: 'od' | 'os',
    form: EyeForm,
    set: (next: EyeForm) => void,
  ): JSX.Element => {
    const sideWord = side === 'od' ? t('sendToLab.right') : t('sendToLab.left')
    const boxes: readonly { readonly key: keyof EyeForm; readonly label: string }[] = [
      { key: 'sphere', label: t('sendToLab.sphere') },
      { key: 'cylinder', label: t('sendToLab.cylinder') },
      { key: 'axis', label: t('sendToLab.axis') },
      { key: 'add', label: t('sendToLab.add') },
      { key: 'pd', label: t('sendToLab.pd') },
      { key: 'seg', label: t('sendToLab.seg') },
    ]
    return (
      <fieldset className="lm-send-lab__eye">
        <legend>{t('sendToLab.eyeLegend', { side: sideWord })}</legend>
        {boxes.map((box) => (
          <label className="lm-field lm-field--inline" key={box.key}>
            <span className="lm-field__label">
              {t('sendToLab.fieldLabel', { field: box.label, side: sideWord })}
            </span>
            {/* `dir="ltr"`: a sign belongs on the left of its digits in every locale
                (`DESIGN-TOKENS.md` §6, `RTL-03`). A mirrored minus is a different prescription. */}
            <input
              className="lm-input"
              dir="ltr"
              inputMode="decimal"
              autoComplete="off"
              value={form[box.key]}
              onChange={(e) => {
                setTypedIn(true)
                set({ ...form, [box.key]: e.currentTarget.value })
              }}
            />
          </label>
        ))}
      </fieldset>
    )
  }

  return (
    <section className="lm-send-lab" aria-labelledby="send-lab-heading">
      <h3 id="send-lab-heading" className="lm-send-lab__heading">
        {t('sendToLab.heading')}
      </h3>

      {jobs.length === 0 ? null : (
        <p className="lm-hint">
          {t('sendToLab.alreadyAtLab', {
            numbers: jobs.map((job) => job.jobNumber).join(', '),
          })}
        </p>
      )}

      <p className="lm-send-lab__frozen">{t('sendToLab.frozenMeans')}</p>
      {rx === undefined ? (
        <p className="lm-hint">{t('sendToLab.noRx')}</p>
      ) : (
        <p className="lm-hint">{t('sendToLab.fromVersion', { version: rx.version })}</p>
      )}
      <p className="lm-hint">{t('sendToLab.noSpec')}</p>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('sendToLab.promisedLabel')}</span>
        <input
          className="lm-input"
          type="date"
          value={promisedAt}
          onChange={(e) => {
            setPromisedAt(e.currentTarget.value)
          }}
        />
      </label>
      <p className="lm-hint">{t('sendToLab.promisedMeans')}</p>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('sendToLab.supplierLabel')}</span>
        <input
          className="lm-input"
          autoComplete="off"
          value={supplierId}
          onChange={(e) => {
            setSupplierId(e.currentTarget.value)
          }}
        />
      </label>
      <p className="lm-hint">{t('sendToLab.supplierMeans')}</p>

      {ownFrameRequired ? null : (
        <label className="lm-send-lab__own-frame-opt-in">
          <input
            type="checkbox"
            checked={ownFrameChosen}
            onChange={(e) => {
              setOwnFrameChosen(e.currentTarget.checked)
              if (!e.currentTarget.checked) setAcknowledged(false)
            }}
          />
          <span>{t('sendToLab.ownFrameOptIn')}</span>
        </label>
      )}
      {ownFrame ? (
        <fieldset className="lm-send-lab__own-frame">
          <legend>{t('sendToLab.ownFrameHeading')}</legend>
          {ownFrameRequired ? <p className="lm-hint">{t('sendToLab.ownFrameRequired')}</p> : null}
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('sendToLab.ownFrameDescriptionLabel')}</span>
            <input
              className="lm-input"
              autoComplete="off"
              value={frameDescription}
              onChange={(e) => {
                setFrameDescription(e.currentTarget.value)
              }}
            />
          </label>
          <p className="lm-hint">{t('sendToLab.ownFrameDescriptionMeans')}</p>
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('sendToLab.ownFramePolicyVersionLabel')}</span>
            <input
              className="lm-input"
              dir="ltr"
              autoComplete="off"
              value={policyVersion}
              onChange={(e) => {
                setPolicyVersion(e.currentTarget.value)
                // A different version is a different text, so a tick given for the old one is
                // not a tick for this one.
                setAcknowledged(false)
              }}
            />
          </label>
          <p className="lm-hint">{t('sendToLab.ownFramePolicyVersionMeans')}</p>
          <label className="lm-send-lab__own-frame-ack">
            <input
              type="checkbox"
              checked={acknowledged}
              disabled={policyVersion.trim() === ''}
              onChange={(e) => {
                setAcknowledged(e.currentTarget.checked)
              }}
            />
            <span>
              {policyVersion.trim() === ''
                ? t('sendToLab.ownFrameAcknowledgeWaiting')
                : t('sendToLab.ownFrameAcknowledge', { version: policyVersion.trim() })}
            </span>
          </label>
        </fieldset>
      ) : null}

      {eyeFields('od', od, setOd)}
      {eyeFields('os', os, setOs)}
      <p className="lm-hint">{t('sendToLab.emptyEyeMeans')}</p>

      {error === null ? null : (
        <p className="lm-error" role="alert">
          {error}
        </p>
      )}
      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      <button
        type="button"
        className="lm-button lm-button--primary"
        // Taken away while this panel's own write is in flight, and given back when it settles or
        // is discarded, which is what `setWriteId(null)` above does. Keyed on this write rather
        // than on the queue being busy: another screen's pending write is not this button's
        // business, and a queue stopped by somebody else's refusal would otherwise lock the form.
        disabled={writeId !== null}
        onClick={() => {
          void submit()
        }}
      >
        {t('sendToLab.submit')}
      </button>
    </section>
  )
}
